import { KeyboardEvent, lazy, Suspense, useEffect, useRef } from 'react';
import { Download, ExternalLink, X } from 'lucide-react';

const PdfViewer = lazy(() => import('../document-detail/PdfViewer'));

interface PreviewDialogProps {
  url: string;
  fileName: string;
  onClose: () => void;
}

/**
 * Accessible PDF preview: modal dialog, Esc closes, focus starts on the close
 * button and returns to whatever opened it. Full screen below `sm`.
 */
export default function PreviewDialog({ url, fileName, onClose }: PreviewDialogProps) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const titleId = 'supplier-preview-title';

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !dialog.current) return;
    const focusable = Array.from(
      dialog.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/70 sm:items-center sm:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="flex h-full w-full max-w-6xl flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[92vh] sm:rounded-lg"
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-slate-900">Vista previa del documento</h2>
            <p className="truncate text-xs text-slate-500" title={fileName}>{fileName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <a href={url} download={fileName} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm text-slate-700 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-blue-500">
              <Download className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">Descargar</span>
            </a>
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm text-slate-700 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-blue-500">
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">Abrir en pestaña nueva</span>
            </a>
            <button ref={closeButton} type="button" onClick={onClose} aria-label="Cerrar vista previa" className="rounded-md p-2 text-slate-600 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-blue-500">
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <Suspense fallback={<p role="status" className="p-8 text-center text-sm text-slate-500">Preparando vista previa…</p>}>
            <PdfViewer url={url} name={fileName} />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
